import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { FileText, BarChart3, TrendingDown, DollarSign, Wallet, AlertTriangle, ClipboardList, PiggyBank, Download, ChevronDown, FileSpreadsheet, FileDown } from 'lucide-react';
import { reportsApi } from '../lib/api';
import { toast } from 'sonner';

const fmt = (v) => v != null ? `R ${Number(v).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}` : '-';

const REPORT_PDF_SLUG = {
  'trial-balance': 'trial-balance',
  'income-statement': 'income-statement',
  'balance-sheet': 'balance-sheet',
  'loan-portfolio': 'loan-portfolio-summary',
  'delinquency': 'delinquency-aging',
  'collection-sheet': 'collection-sheet',
  'expected-payments': 'expected-payments',
  'written-off': 'written-off-loans',
  'savings-summary': 'client-savings-summary',
};

const FinancialReportsPage = () => {
  const [activeReport, setActiveReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (exportRef.current && !exportRef.current.contains(e.target)) setExportOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const reports = [
    { id: 'trial-balance', name: 'Trial Balance', icon: BarChart3, desc: 'Debit and credit balances for all accounts', api: reportsApi.trialBalance },
    { id: 'income-statement', name: 'Income Statement', icon: TrendingDown, desc: 'Revenue, expenses, and net income (P&L)', api: reportsApi.incomeStatement },
    { id: 'balance-sheet', name: 'Balance Sheet', icon: DollarSign, desc: 'Assets, liabilities, and equity position', api: reportsApi.balanceSheet },
    { id: 'loan-portfolio', name: 'Loan Portfolio Summary', icon: Wallet, desc: 'Portfolio by status, product, and performance', api: reportsApi.loanPortfolioSummary },
    { id: 'delinquency', name: 'Delinquency Aging', icon: AlertTriangle, desc: 'Overdue loan classification by aging bucket', api: reportsApi.delinquencyAging },
    { id: 'collection-sheet', name: 'Collection Sheet', icon: ClipboardList, desc: 'Expected collections for active loans', api: reportsApi.collectionSheet },
    { id: 'expected-payments', name: 'Expected Payments', icon: FileText, desc: 'Upcoming payment projections by period', api: reportsApi.expectedPayments },
    { id: 'written-off', name: 'Written-Off Loans', icon: AlertTriangle, desc: 'Loans that have been written off', api: reportsApi.writtenOffLoans },
    { id: 'savings-summary', name: 'Client Savings Summary', icon: PiggyBank, desc: 'Deposit balances by product type', api: reportsApi.clientSavingsSummary },
  ];

  const generateReport = async (report) => {
    setActiveReport(report.id);
    setLoading(true);
    setReportData(null);
    try {
      const res = await report.api();
      setReportData(res.data);
    } catch (e) {
      toast.error('Failed to generate report');
      console.error(e);
    }
    setLoading(false);
  };

  const downloadReport = async (format) => {
    if (!activeReport) return;
    setPdfLoading(true);
    setExportOpen(false);
    try {
      const slug = REPORT_PDF_SLUG[activeReport];
      const apiMap = { pdf: reportsApi.downloadPdf, excel: reportsApi.downloadExcel, csv: reportsApi.downloadCsv, ixbrl: reportsApi.downloadIxbrl };
      const extMap = { pdf: 'pdf', excel: 'xlsx', csv: 'csv', ixbrl: 'xhtml' };
      const mimeMap = { pdf: 'application/pdf', excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', csv: 'text/csv', ixbrl: 'application/xhtml+xml' };
      const res = await apiMap[format](slug);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mimeMap[format] }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${slug.replace(/-/g, '_')}_${new Date().toISOString().slice(0,10)}.${extMap[format]}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(`Failed to download ${format.toUpperCase()}`);
      console.error(e);
    }
    setPdfLoading(false);
  };

  return (
    <div className="space-y-6 fade-in" data-testid="financial-reports-page">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
        <span>Reports</span><span>/</span><span className="text-[#1f1f1f] font-medium">Financial Reports</span>
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-1">STANDARD REPORTS</p>
        <h1 className="font-display text-2xl font-bold text-[#1f1f1f]">Financial Reports</h1>
      </div>

      {/* Report Selection */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="report-cards">
        {reports.map(r => (
          <button
            key={r.id}
            onClick={() => generateReport(r)}
            className={`text-left p-4 border transition-all group ${activeReport === r.id ? 'border-[#C5A059] bg-[#FFFDF5]' : 'border-gray-200 hover:border-[#C5A059] hover:bg-[#FFFDF5]'}`}
            data-testid={`report-card-${r.id}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <r.icon className="w-4 h-4 text-[#C5A059]" />
              <span className="text-sm font-medium text-[#1f1f1f]">{r.name}</span>
            </div>
            <p className="text-xs text-gray-500">{r.desc}</p>
          </button>
        ))}
      </div>

      {/* Report Output */}
      {loading && <div className="bg-white border border-gray-200 p-8 text-center text-gray-400">Generating report...</div>}

      {reportData && !loading && (
        <div className="bg-white border border-gray-200 p-6" data-testid="report-output">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-[#1f1f1f]">{reportData.report_name}</h3>
              <p className="text-xs text-gray-500">Generated: {new Date(reportData.generated_at).toLocaleString()}</p>
            </div>
            <div className="relative" ref={exportRef}>
              <Button
                onClick={() => setExportOpen(!exportOpen)}
                disabled={pdfLoading}
                variant="outline"
                className="border-[#C5A059] text-[#C5A059] hover:bg-[#FFFDF5] gap-2"
                data-testid="export-report-btn"
              >
                <Download className="w-4 h-4" />
                {pdfLoading ? 'Exporting...' : 'Export'}
                <ChevronDown className="w-3 h-3" />
              </Button>
              {exportOpen && (
                <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-gray-200 shadow-lg z-50" data-testid="export-dropdown">
                  <button onClick={() => downloadReport('pdf')} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-[#1f1f1f] hover:bg-[#FFFDF5] transition-colors" data-testid="export-pdf-btn">
                    <FileDown className="w-4 h-4 text-red-500" /> PDF Document
                  </button>
                  <button onClick={() => downloadReport('excel')} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-[#1f1f1f] hover:bg-[#FFFDF5] transition-colors" data-testid="export-excel-btn">
                    <FileSpreadsheet className="w-4 h-4 text-green-600" /> Excel (.xlsx)
                  </button>
                  <button onClick={() => downloadReport('csv')} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-[#1f1f1f] hover:bg-[#FFFDF5] transition-colors" data-testid="export-csv-btn">
                    <FileText className="w-4 h-4 text-blue-500" /> CSV
                  </button>
                  <button onClick={() => downloadReport('ixbrl')} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-[#1f1f1f] hover:bg-[#FFFDF5] transition-colors border-t border-gray-100" data-testid="export-ixbrl-btn">
                    <FileText className="w-4 h-4 text-purple-600" /> iXBRL (.xhtml)
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Trial Balance */}
          {activeReport === 'trial-balance' && (
            <div>
              <table className="w-full" data-testid="trial-balance-table">
                <thead><tr className="bg-gray-50 border-b">
                  <th className="text-left px-4 py-2 text-xs font-semibold uppercase text-gray-500">Account</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold uppercase text-gray-500">Type</th>
                  <th className="text-right px-4 py-2 text-xs font-semibold uppercase text-gray-500">Debit</th>
                  <th className="text-right px-4 py-2 text-xs font-semibold uppercase text-gray-500">Credit</th>
                </tr></thead>
                <tbody>
                  {reportData.accounts?.map((a, i) => (
                    <tr key={i} className="border-b"><td className="px-4 py-2 text-sm"><span className="font-mono text-gray-400 mr-2">{a.account_number}</span>{a.account_name}</td>
                      <td className="px-4 py-2 text-xs text-gray-500 capitalize">{a.account_type}</td>
                      <td className="px-4 py-2 text-right font-mono text-sm">{a.debit > 0 ? fmt(a.debit) : '-'}</td>
                      <td className="px-4 py-2 text-right font-mono text-sm">{a.credit > 0 ? fmt(a.credit) : '-'}</td></tr>
                  ))}
                </tbody>
                <tfoot><tr className="bg-gray-50 font-semibold">
                  <td colSpan={2} className="px-4 py-2 text-sm">Total</td>
                  <td className="px-4 py-2 text-right font-mono text-sm">{fmt(reportData.total_debit)}</td>
                  <td className="px-4 py-2 text-right font-mono text-sm">{fmt(reportData.total_credit)}</td>
                </tr></tfoot>
              </table>
              <div className={`mt-2 px-4 py-2 text-xs ${reportData.balanced ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                {reportData.balanced ? 'Trial balance is balanced' : 'Trial balance is NOT balanced — review entries'}
              </div>
            </div>
          )}

          {/* Income Statement */}
          {activeReport === 'income-statement' && (
            <div className="space-y-4">
              <div><h4 className="text-xs font-semibold uppercase text-gray-500 mb-2">Revenue</h4>
                {reportData.revenue?.map((r, i) => <div key={i} className="flex justify-between px-4 py-1 text-sm border-b"><span>{r.name}</span><span className="font-mono">{fmt(r.amount)}</span></div>)}
                <div className="flex justify-between px-4 py-2 font-semibold bg-emerald-50"><span>Total Revenue</span><span className="font-mono">{fmt(reportData.total_revenue)}</span></div>
              </div>
              <div><h4 className="text-xs font-semibold uppercase text-gray-500 mb-2">Expenses</h4>
                {reportData.expenses?.map((e, i) => <div key={i} className="flex justify-between px-4 py-1 text-sm border-b"><span>{e.name}</span><span className="font-mono">{fmt(e.amount)}</span></div>)}
                <div className="flex justify-between px-4 py-2 font-semibold bg-red-50"><span>Total Expenses</span><span className="font-mono">{fmt(reportData.total_expenses)}</span></div>
              </div>
              <div className={`flex justify-between px-4 py-3 text-lg font-bold ${reportData.profit ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                <span>Net {reportData.profit ? 'Income' : 'Loss'}</span><span className="font-mono">{fmt(reportData.net_income)}</span>
              </div>
            </div>
          )}

          {/* Balance Sheet */}
          {activeReport === 'balance-sheet' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-xs font-semibold uppercase text-gray-500 mb-2">Assets</h4>
                {reportData.assets?.map((a, i) => <div key={i} className="flex justify-between px-4 py-1 text-sm border-b"><span>{a.name}</span><span className="font-mono">{fmt(a.amount)}</span></div>)}
                <div className="flex justify-between px-4 py-2 font-semibold bg-blue-50"><span>Total Assets</span><span className="font-mono">{fmt(reportData.total_assets)}</span></div>
              </div>
              <div>
                <h4 className="text-xs font-semibold uppercase text-gray-500 mb-2">Liabilities</h4>
                {reportData.liabilities?.map((l, i) => <div key={i} className="flex justify-between px-4 py-1 text-sm border-b"><span>{l.name}</span><span className="font-mono">{fmt(l.amount)}</span></div>)}
                <div className="flex justify-between px-4 py-2 font-semibold bg-amber-50"><span>Total Liabilities</span><span className="font-mono">{fmt(reportData.total_liabilities)}</span></div>
                <h4 className="text-xs font-semibold uppercase text-gray-500 mb-2 mt-4">Equity</h4>
                {reportData.equity?.map((e, i) => <div key={i} className="flex justify-between px-4 py-1 text-sm border-b"><span>{e.name}</span><span className="font-mono">{fmt(e.amount)}</span></div>)}
                <div className="flex justify-between px-4 py-2 font-semibold bg-gray-50"><span>Total Equity</span><span className="font-mono">{fmt(reportData.total_equity)}</span></div>
              </div>
            </div>
          )}

          {/* Loan Portfolio Summary */}
          {activeReport === 'loan-portfolio' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 bg-gray-50 border"><p className="text-xs text-gray-500">Total Loans</p><p className="text-xl font-bold">{reportData.total_loans}</p></div>
                <div className="p-3 bg-gray-50 border"><p className="text-xs text-gray-500">Total Disbursed</p><p className="text-xl font-bold">{fmt(reportData.total_disbursed)}</p></div>
                <div className="p-3 bg-gray-50 border"><p className="text-xs text-gray-500">Outstanding</p><p className="text-xl font-bold">{fmt(reportData.total_outstanding)}</p></div>
                <div className="p-3 bg-gray-50 border"><p className="text-xs text-gray-500">Repayment Rate</p><p className="text-xl font-bold">{reportData.repayment_rate}%</p></div>
              </div>
              <h4 className="text-xs font-semibold uppercase text-gray-500">By Status</h4>
              {Object.entries(reportData.by_status || {}).map(([status, data]) => (
                <div key={status} className="flex justify-between px-4 py-2 text-sm border-b">
                  <span className="capitalize font-medium">{status}</span>
                  <span className="text-gray-600">{data.count} loans / {fmt(data.outstanding)} outstanding</span>
                </div>
              ))}
            </div>
          )}

          {/* Delinquency Aging */}
          {activeReport === 'delinquency' && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-gray-50 border"><p className="text-xs text-gray-500">Total Outstanding</p><p className="text-xl font-bold">{fmt(reportData.total_outstanding)}</p></div>
                <div className="p-3 bg-red-50 border border-red-200"><p className="text-xs text-gray-500">Total Delinquent</p><p className="text-xl font-bold text-red-600">{fmt(reportData.total_delinquent)}</p></div>
                <div className="p-3 bg-gray-50 border"><p className="text-xs text-gray-500">Delinquency Rate</p><p className="text-xl font-bold">{reportData.delinquency_rate}%</p></div>
              </div>
              <table className="w-full" data-testid="delinquency-table">
                <thead><tr className="bg-gray-50 border-b">
                  <th className="text-left px-4 py-2 text-xs font-semibold uppercase text-gray-500">Bucket</th>
                  <th className="text-right px-4 py-2 text-xs font-semibold uppercase text-gray-500">Count</th>
                  <th className="text-right px-4 py-2 text-xs font-semibold uppercase text-gray-500">Outstanding</th>
                </tr></thead>
                <tbody>
                  {Object.entries(reportData.buckets || {}).map(([bucket, data]) => (
                    <tr key={bucket} className="border-b"><td className="px-4 py-2 text-sm capitalize">{bucket.replace(/_/g, ' ')}</td>
                      <td className="px-4 py-2 text-right font-mono text-sm">{data.count}</td>
                      <td className="px-4 py-2 text-right font-mono text-sm">{fmt(data.outstanding)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Collection Sheet */}
          {activeReport === 'collection-sheet' && (
            <div>
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200">
                <span className="text-sm font-medium text-blue-700">Total Expected: {fmt(reportData.total_expected)} from {reportData.collection_count} loans</span>
              </div>
              <table className="w-full">
                <thead><tr className="bg-gray-50 border-b">
                  <th className="text-left px-4 py-2 text-xs font-semibold uppercase text-gray-500">Loan #</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold uppercase text-gray-500">Client</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold uppercase text-gray-500">Phone</th>
                  <th className="text-right px-4 py-2 text-xs font-semibold uppercase text-gray-500">Outstanding</th>
                  <th className="text-right px-4 py-2 text-xs font-semibold uppercase text-gray-500">Monthly Due</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold uppercase text-gray-500">Rate Mode</th>
                </tr></thead>
                <tbody>
                  {reportData.collections?.map((c, i) => (
                    <tr key={i} className="border-b"><td className="px-4 py-2 font-mono text-xs">{c.loan_number}</td>
                      <td className="px-4 py-2 text-sm">{c.client_name}</td>
                      <td className="px-4 py-2 text-xs font-mono text-gray-500">{c.client_phone}</td>
                      <td className="px-4 py-2 text-right font-mono text-sm">{fmt(c.outstanding)}</td>
                      <td className="px-4 py-2 text-right font-mono text-sm font-medium">{fmt(c.monthly_payment)}</td>
                      <td className="px-4 py-2"><span className={`text-xs px-1.5 py-0.5 rounded ${c.rate_mode === 'floating' ? 'bg-blue-50 text-blue-700' : 'bg-gray-50 text-gray-600'}`}>{c.rate_mode}</span></td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Expected Payments */}
          {activeReport === 'expected-payments' && (
            <div className="space-y-4">
              <div className="p-3 bg-blue-50 border border-blue-200"><span className="text-sm font-medium text-blue-700">Total Expected: {fmt(reportData.total_expected)}</span></div>
              {Object.entries(reportData.periods || {}).map(([period, data]) => (
                <div key={period} className="flex justify-between px-4 py-3 border-b">
                  <span className="capitalize font-medium text-sm">{period.replace(/_/g, ' ')}</span>
                  <span className="font-mono text-sm">{fmt(data.total)} ({data.count} loans)</span>
                </div>
              ))}
            </div>
          )}

          {/* Written-Off Loans */}
          {activeReport === 'written-off' && (
            <div>
              <div className="mb-4 p-3 bg-red-50 border border-red-200"><span className="text-sm font-medium text-red-700">Total Written Off: {fmt(reportData.total_written_off)} ({reportData.loan_count} loans)</span></div>
              {reportData.loans?.length > 0 ? (
                <table className="w-full">
                  <thead><tr className="bg-gray-50 border-b">
                    <th className="text-left px-4 py-2 text-xs font-semibold uppercase text-gray-500">Loan #</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold uppercase text-gray-500">Client</th>
                    <th className="text-right px-4 py-2 text-xs font-semibold uppercase text-gray-500">Original Amount</th>
                    <th className="text-right px-4 py-2 text-xs font-semibold uppercase text-gray-500">Written Off</th>
                  </tr></thead>
                  <tbody>{reportData.loans.map((l, i) => (
                    <tr key={i} className="border-b"><td className="px-4 py-2 font-mono text-xs">{l.loan_number}</td>
                      <td className="px-4 py-2 text-sm">{l.client_name}</td>
                      <td className="px-4 py-2 text-right font-mono text-sm">{fmt(l.original_amount)}</td>
                      <td className="px-4 py-2 text-right font-mono text-sm text-red-600">{fmt(l.outstanding_at_writeoff)}</td></tr>
                  ))}</tbody>
                </table>
              ) : <p className="text-gray-400 text-center py-4">No written-off loans</p>}
            </div>
          )}

          {/* Savings Summary */}
          {activeReport === 'savings-summary' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 border"><p className="text-xs text-gray-500">Total Deposits</p><p className="text-xl font-bold">{fmt(reportData.total_deposits)}</p></div>
                <div className="p-3 bg-gray-50 border"><p className="text-xs text-gray-500">Total Accounts</p><p className="text-xl font-bold">{reportData.total_accounts}</p></div>
              </div>
              {Object.entries(reportData.by_product || {}).map(([product, data]) => (
                <div key={product} className="flex justify-between px-4 py-3 border-b">
                  <span className="capitalize font-medium text-sm">{product.replace(/_/g, ' ')}</span>
                  <span className="font-mono text-sm">{fmt(data.total)} ({data.count} accounts)</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FinancialReportsPage;
